import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Bike, Plane, Ship, Truck } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import RateCalculator from "../components/RateCalculator";
import { FaqSection, PartnersMarquee, TestimonialsSection } from "../components/home/ContentSections";
import ShopStrip from "../components/home/ShopStrip";
import BranchStrip from "../components/home/BranchStrip";
import BranchFinder from "../components/home/BranchFinder";
import ActionPanel from "../components/home/ActionPanel";
import TrustStrip from "../components/home/TrustStrip";
import EditorialIntro from "../components/home/EditorialIntro";
import GlobalServices from "../components/home/GlobalServices";
import JourneyTimeline from "../components/home/JourneyTimeline";
import Destinations from "../components/home/Destinations";
import WhyIndiaFast from "../components/home/WhyIndiaFast";
import BusinessSection from "../components/home/BusinessSection";
import TrackingSection from "../components/home/TrackingSection";
import NewsInsights from "../components/home/NewsInsights";
import FinalCta from "../components/home/FinalCta";
import ScrollStory from "../components/home/ScrollStory";
import api from "../lib/api";
import { usePageMeta } from "../components/service/ServiceBits";

const HERO_IMG = "/videos/hero_poster.jpg";

const HERO_STRIP = [
  { step: "01", label: "Air Express", icon: Plane, to: "/international-courier", testid: "hero-strip-air" },
  { step: "02", label: "Road Freight", icon: Truck, to: "/domestic-courier", testid: "hero-strip-road" },
  { step: "03", label: "Sea Cargo", icon: Ship, to: "/business-courier", testid: "hero-strip-sea" },
  { step: "04", label: "Door-to-Door", icon: Bike, to: "/pickup-request", testid: "hero-strip-door" },
];

export default function Home() {
  usePageMeta(
    "INDIAFAST Global Couriers | Domestic & International Courier Services in India",
    "Ship across 29,000+ Indian pincodes and 220+ countries with doorstep pickup, live tracking and transparent GST-inclusive pricing. Book a courier in 60 seconds.",
    "/"
  );
  const [home, setHome] = useState(null);
  useEffect(() => {
    api.get("/public/home").then((r) => setHome(r.data)).catch(() => setHome({}));
  }, []);
  const hero = home?.cms?.hero || {};
  const navigate = useNavigate();

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div id="top" className="min-h-screen bg-white">
      <Navbar />

      <section className="relative flex min-h-screen flex-col overflow-hidden bg-[#F2F1EE]" data-testid="hero-section">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={HERO_IMG} alt="" className="kenburns-a h-full w-full object-cover" />
          <video className="hero-video absolute inset-0 h-full w-full object-cover"
                 autoPlay muted loop playsInline preload="metadata" poster={HERO_IMG} data-testid="hero-bg-video">
            <source src="/videos/hero.mp4" type="video/mp4" />
            <source src="/videos/hero.webm" type="video/webm" />
          </video>
        </div>
        <div className="relative mx-auto flex w-full max-w-7xl flex-1 items-end px-4 pb-10 pt-28 sm:px-6">
          <div className="animate-fade-up w-full max-w-xl rounded-3xl border border-white/60 bg-white/95 p-6 shadow-2xl shadow-stone-300/60 backdrop-blur sm:p-9">
            <p className="flex items-center gap-2 text-xs font-semibold text-slate-500">
              <span className="h-2 w-2 rounded-full bg-saffron" /> INDIAFAST Global Couriers
            </p>
            <h1 data-testid="hero-headline"
                className="mt-4 font-heading text-4xl font-medium leading-[1.06] tracking-tight text-navy sm:text-5xl lg:text-6xl">
              {hero.headline || "India to the World. Delivered with Confidence."}
            </h1>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-500 sm:text-base">
              {hero.subtext || "Tailored courier and cargo services — domestic and international. However your shipment needs to travel, we deliver it safely to its destination."}
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <button onClick={() => navigate("/register")} data-testid="hero-ship-now-btn"
                      className="group inline-flex items-center gap-2 rounded-full bg-saffron py-2 pl-6 pr-2 text-sm font-bold text-white transition-colors hover:bg-saffron-hover">
                Ship Now
                <span className="grid h-8 w-8 place-items-center rounded-full bg-white/20 transition-transform group-hover:translate-x-0.5">
                  <ArrowRight className="h-4 w-4" />
                </span>
              </button>
              <button onClick={() => scrollTo("calculator")} data-testid="hero-quote-btn"
                      className="group inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white py-2 pl-6 pr-2 text-sm font-bold text-navy transition-colors hover:border-saffron hover:text-saffron">
                Get a Quote
                <span className="grid h-8 w-8 place-items-center rounded-full bg-saffron/10 text-saffron transition-colors group-hover:bg-saffron group-hover:text-white">
                  <ArrowRight className="h-4 w-4" />
                </span>
              </button>
              <button onClick={() => navigate("/track")} data-testid="hero-track-btn"
                      className="rounded-full px-5 py-2.5 text-sm font-bold text-slate-500 transition-colors hover:text-saffron">
                Track Shipment
              </button>
            </div>
            <BranchFinder />
          </div>
        </div>
        <div className="relative border-t border-navy/10 bg-white/85 backdrop-blur" data-testid="hero-services-strip">
          <div className="mx-auto grid max-w-7xl grid-cols-2 px-4 sm:grid-cols-5 sm:px-6">
            {HERO_STRIP.map((s) => (
              <button key={s.step} onClick={() => navigate(s.to)} data-testid={s.testid}
                      className="group flex items-center gap-3 border-l border-navy/10 px-4 py-4 text-left transition-colors first:border-l-0 hover:bg-saffron-light sm:py-5">
                <span className="font-heading text-sm font-bold text-saffron">{s.step}</span>
                <s.icon className="h-4 w-4 text-slate-400 transition-colors group-hover:text-saffron" />
                <span className="text-sm font-semibold text-navy transition-colors group-hover:text-saffron">{s.label}</span>
              </button>
            ))}
            <button onClick={() => scrollTo("services")} data-testid="hero-strip-all"
                    className="group col-span-2 flex items-center justify-center gap-2 border-navy/10 px-4 py-4 text-sm font-bold text-saffron transition-colors hover:bg-saffron-light sm:col-span-1 sm:border-l sm:py-5">
              All Services <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </section>

      <ActionPanel />
      <ScrollStory />
      <TrustStrip />
      <EditorialIntro />
      <GlobalServices />
      <JourneyTimeline />
      <Destinations />

      <section id="calculator" className="scroll-mt-24 bg-[#F4F3F1] py-20 sm:py-28" data-testid="home-calculator-section">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <p className="flex items-center gap-2 text-xs font-semibold text-slate-500">
            <span className="h-2 w-2 rounded-full bg-saffron" /> Live pricing
          </p>
          <h2 className="mt-4 font-heading text-3xl font-medium tracking-tight text-navy sm:text-4xl">
            Calculate your <span className="text-saffron">shipping cost</span>
          </h2>
          <p className="mt-3 max-w-xl text-sm leading-relaxed text-slate-500 sm:text-base">
            Real-time rates from the INDIAFAST rate engine — domestic and international, inclusive of GST.
          </p>
          <div className="mt-10">
            <RateCalculator />
          </div>
        </div>
      </section>

      <WhyIndiaFast stats={home?.stats} />
      <PartnersMarquee partners={home?.partners} />
      <BusinessSection />
      <ShopStrip />
      <TrackingSection />
      <BranchStrip />
      <TestimonialsSection testimonials={home?.cms?.testimonials} />
      <NewsInsights />
      <div id="faqs" className="scroll-mt-20"><FaqSection faqs={home?.cms?.faqs} /></div>
      <FinalCta />
      <Footer contact={home?.contact} />
    </div>
  );
}
